import { useEffect, useRef, useState } from "react";
import { ContactDetails } from "../ContactDetails/ContactDetails";
import { deepEqual, isFormEmpty, userDataInfo, validate } from "../utils";
import classes from "./UserForm.module.css";
import { FormData } from "../utils";
import { useMultiStepForm } from "../Provider/MultiStepFormContext";
import { useAlternateProductContext } from "../User/AlternateProductContext";
import { useNavigate } from "react-router-dom";
import { useAddUserDetails } from "../../Mutations/useAddUserDetails";
import { ErrorPopup } from "../Error/ErrorPopup";
import { LoaderOverlay } from "../Loader/LoadingOverlay";
import { t } from "i18next";

const defaultForm: FormData = {
  dealerName: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  address: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "",
    zipcode: "",
  },
  preferredContact: undefined,
};
export const UserForm = () => {
  const navigate = useNavigate();
  const isDirty = useRef(false);
  const addUserInfo = useAddUserDetails();
  const [loading, setLoading] = useState(false);
  const [displayErrorPopup, setDisplayErrorPopup] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [form, setForm] = useState<FormData>(defaultForm);
  const selectedCountry = sessionStorage.getItem("country") ?? "";
  // Detect form changes
  useEffect(() => {
    isDirty.current = !deepEqual(form, defaultForm);
  }, [form]);

  // ✅ Add browser unload blocker
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty.current) {
        e.preventDefault();
        e.returnValue = ""; // Required for Chrome
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);
  const { providerFormData, clearFormData } = useMultiStepForm();
  const { alternateProductData, clearAlternateProductData } =
    useAlternateProductContext();

  const handleSubmit = async () => {
    const isValid = validate(form, setErrors);
    if (!isValid) {
      // alert(t("form.alert_error"));
      return;
    }
    const customerInfo: userDataInfo = {
      consigneeId: providerFormData.consigneeId,
      consigneeIdValid: true,
      noConsigneeId: providerFormData.noConsigneeId,
      userAcknowledgement: providerFormData.dealerAcknowlegement,
      numInPosession: alternateProductData.quantity,
      numUnitsReturn: alternateProductData.quantity,
      // numReturnUnits:
      //   alternateProductData.replacementProductButton +
      //   alternateProductData.replacementProductSwitch,
      numSwitchReplace: alternateProductData.replacementProductSwitch,
      numButtonReplace: alternateProductData.replacementProductButton,
      noReplacement: alternateProductData.noReplacement,
      contactMetoDiscuss: alternateProductData.contactMe,
      serialNumber: alternateProductData.serialNumber,
      noReplacementReason: alternateProductData.noReplacementReason || "",
      dealerName: form.dealerName,
      firstName: form.firstName,
      lastName: form.lastName,
      phone: form.phone,
      email: form.email,
      line1: form.address.line1 || "Not available",
      line2: form.address.line2 || "Not available",
      city: form.address.city || "Not available",
      state: form.address.state || "Not available",
      country: form.address.country || "Not available",
      zipcode: form.address.zipcode || "Not available",
      preferredContactType: form.preferredContact ?? "",
      selectedCountry: selectedCountry,
    };
    setLoading(true);
    try {
      const result = await addUserInfo.mutateAsync(customerInfo);
      if (result.status === 200) {
        clearFormData();
        clearAlternateProductData();
        navigate("/confirmation");
      } else {
        setDisplayErrorPopup(true);
      }
    } catch (error) {
      setDisplayErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };
  const handleBack = () => {
    navigate("/alternate-product");
  };

  return (
    <div className={classes.container}>
      <h2 className={classes.title}>{t("form.page_title")}</h2>
      <ContactDetails
        form={form}
        setForm={setForm}
        errors={errors}
        isUserForm
      />
      <div className={"mandatoryText"}>* {t("common.required")}</div>
      <div className={classes.nextContainer}>
        <button className={classes.backButton} onClick={handleBack}>
          {t("common.back")}
        </button>
        <button
          onClick={handleSubmit}
          className={classes.nextButton}
          disabled={isFormEmpty(form)}
        >
          Submit
        </button>
      </div>

      {displayErrorPopup && <ErrorPopup />}
      {loading && <LoaderOverlay />}
    </div>
  );
};
